import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  /** Tooltip text or content shown on hover/focus. */
  content: ReactNode;
  /** The trigger element the tooltip is attached to. */
  children: ReactNode;
  className?: string;
  /**
   * Renders the bubble into `document.body` so `overflow` containers (modal bodies,
   * scrolling lists) cannot clip it. Positioned above the trigger via
   * `getBoundingClientRect`, closed on mouseleave/blur and on any scroll.
   */
  portal?: boolean;
}

const BUBBLE_CLASSES =
  'pointer-events-none w-max max-w-xs rounded-md bg-slate-900 px-2.5 py-1.5 text-[11px] leading-snug text-white shadow-lg';

/**
 * Lightweight hover/focus tooltip. Renders a flat dark popover above the trigger.
 * Default is pure CSS (group-hover/focus) — no state, no portal. Use for short hints
 * such as plan-gating explanations; pass `portal` inside clipping scroll containers.
 */
export function Tooltip({ content, children, className = '', portal = false }: TooltipProps) {
  if (portal) {
    return (
      <PortalTooltip content={content} className={className}>
        {children}
      </PortalTooltip>
    );
  }

  return (
    <span className={`group relative inline-flex ${className}`} tabIndex={0}>
      {children}
      <span
        role="tooltip"
        className={`absolute left-1/2 bottom-full z-50 mb-2 -translate-x-1/2 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100 ${BUBBLE_CLASSES}`}
      >
        {content}
      </span>
    </span>
  );
}

function PortalTooltip({ content, children, className = '' }: Omit<TooltipProps, 'portal'>) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  // Viewport coordinates of the trigger's top center; null while hidden.
  const [anchor, setAnchor] = useState<{ left: number; top: number } | null>(null);

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setAnchor({ left: rect.left + rect.width / 2, top: rect.top });
  };
  const hide = () => setAnchor(null);

  // `position: fixed` does not follow the trigger — any scroll would leave the bubble
  // floating in place, so it closes instead. Capture phase catches nested containers.
  useEffect(() => {
    if (!anchor) return;
    const close = () => setAnchor(null);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [anchor]);

  return (
    <span
      ref={triggerRef}
      className={`inline-flex ${className}`}
      tabIndex={0}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {anchor &&
        createPortal(
          <span
            role="tooltip"
            // Above the Modal layer (z-[80] in Modal.tsx).
            style={{ left: anchor.left, top: anchor.top - 8 }}
            className={`fixed z-[90] -translate-x-1/2 -translate-y-full ${BUBBLE_CLASSES}`}
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
}

Tooltip.displayName = 'Tooltip';
